import React, { useEffect, useState } from 'react';
import { UserCog, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, StatusBadge, Modal, Field } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { useI18n } from '../i18n/I18nContext';
import api from '../api/client';

const ROLE_KEYS = { ADMIN: 'usr.roleAdmin', SUPERVISOR: 'usr.roleSupervisor', OPERATOR: 'usr.roleOperator', PENDING: 'usr.rolePending' };
const ROLE_META = { ADMIN: { cls: 's-hold' }, SUPERVISOR: { cls: 's-progress' }, OPERATOR: { cls: 's-completed' }, PENDING: { cls: 's-problem' } };
const EMPTY = { name: '', email: '', password: '', role: 'OPERATOR', warehouse_id: '' };

function fmt(ts) { try { return new Date(ts).toLocaleDateString(); } catch (e) { return '—'; } }

export default function UserManagement() {
  const { t } = useI18n();
  const roleLabel = (r) => t(ROLE_KEYS[r] || 'usr.roleOperator');
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.listUsers().then(setUsers).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api.listWarehouses().then(setWarehouses).catch(() => {}); }, []);
  useRealtime(() => load());

  const whName = (id) => warehouses.find((w) => w.id === id)?.name || '';

  const save = async () => {
    setSaving(true);
    try {
      const created = await api.createUser({ ...form, warehouse_id: form.warehouse_id || null });
      setUsers((p) => [...p, created]);
      toast.success(t('usr.added').replace('{name}', created.name));
      setModal(false); setForm(EMPTY);
    } catch (e) { toast.error(e?.response?.data?.detail || t('usr.failAdd')); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(t('usr.removeConfirm').replace('{name}', u.name))) return;
    setUsers((p) => p.filter((x) => x.id !== u.id));
    try { await api.deleteUser(u.id); toast.success(t('usr.removed').replace('{name}', u.name)); }
    catch (e) { toast.error(e?.response?.data?.detail || t('common.deleteFailed')); load(); }
  };

  const changeRole = async (u, role) => {
    if (!role || role === u.role) return;
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, role } : x)));
    try { await api.updateUser(u.id, { role }); toast.success(`${u.name} → ${roleLabel(role)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t('usr.updateFailed')); load(); }
  };

  const changeWarehouse = async (u, warehouse_id) => {
    const val = warehouse_id || null;
    if (val === (u.warehouse_id || null)) return;
    setUsers((p) => p.map((x) => (x.id === u.id ? { ...x, warehouse_id: val } : x)));
    try { await api.updateUser(u.id, { warehouse_id: val }); toast.success(`${u.name} → ${val ? whName(val) : t('usr.allFactories')}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t('usr.updateFailed')); load(); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={UserCog} title={t('usr.title')} subtitle={`${users.length} ${t('usr.sub')}`}
        right={<button className="kmt-btn-gold flex items-center gap-2" onClick={() => { setForm(EMPTY); setModal(true); }} data-testid="add-user-btn"><Plus size={16} />{t('usr.addUser')}</button>} />
      <div className="p-8">
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">{t('col.name')}</th><th className="kmt-th">{t('col.email')}</th><th className="kmt-th">{t('col.role')}</th><th className="kmt-th">{t('col.factory')}</th><th className="kmt-th">{t('col.joined')}</th><th className="kmt-th">{t('col.action')}</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="kmt-td" data-label="Name"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-md flex items-center justify-center font-display font-bold" style={{ background: 'var(--kmt-gold)', color: '#17140a' }}>{(u.name || '?')[0]}</div>{u.name}</div></td>
                    <td className="kmt-td font-mono-k" data-label="Email" style={{ color: 'var(--kmt-muted)' }}>{u.email}</td>
                    <td className="kmt-td" data-label="Role"><StatusBadge meta={{ ...(ROLE_META[u.role] || ROLE_META.OPERATOR), label: roleLabel(u.role) }} /></td>
                    <td className="kmt-td" data-label="Factory">
                      <select
                        value={u.warehouse_id || ''}
                        onChange={(e) => changeWarehouse(u, e.target.value)}
                        disabled={u.role === 'ADMIN'}
                        className="kmt-input" style={{ width: 160, padding: '6px 8px' }}
                        title={u.role === 'ADMIN' ? t('usr.adminTip') : t('usr.lockTip')}
                        data-testid={`warehouse-select-${u.id}`}>
                        <option value="">{u.role === 'ADMIN' ? t('usr.allFactories') : t('usr.notAssigned')}</option>
                        {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </td>
                    <td className="kmt-td" data-label="Joined" style={{ color: 'var(--kmt-muted)' }}>{fmt(u.created_at)}</td>
                    <td className="kmt-td" data-label="Action">
                      <div className="flex items-center gap-3">
                        <select
                          value={['ADMIN', 'SUPERVISOR', 'OPERATOR'].includes(u.role) ? u.role : ''}
                          onChange={(e) => changeRole(u, e.target.value)}
                          className="kmt-input" style={{ width: 160, padding: '6px 8px' }}
                          data-testid={`role-select-${u.id}`}>
                          {u.role === 'PENDING' && <option value="">{t('usr.assignRole')}</option>}
                          <option value="OPERATOR">{t('usr.roleOperator')}</option>
                          <option value="SUPERVISOR">{t('usr.roleSupervisor')}</option>
                          <option value="ADMIN">{t('usr.roleAdmin')}</option>
                        </select>
                        <button onClick={() => remove(u)} title={t('usr.removeTip')} data-testid={`user-delete-${u.id}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={t('usr.addUser')}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(false)}>{t('common.cancel')}</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? t('usr.saving') : t('usr.save')}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Field label={t('login.fullName')}><input className="kmt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
          <div className="sm:col-span-2"><Field label={t('login.email')}><input className="kmt-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field></div>
          <Field label={t('login.password')}><input className="kmt-input" type="text" value={form.password} placeholder={t('usr.pwHint')} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label={t('col.role')}>
            <select className="kmt-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="OPERATOR">{t('usr.roleOperator')}</option>
              <option value="SUPERVISOR">{t('usr.roleSupervisor')}</option>
              <option value="ADMIN">{t('usr.roleAdmin')}</option>
            </select>
          </Field>
          <Field label={t('usr.factoryWarehouse')}>
            <select className="kmt-input" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} data-testid="add-user-warehouse-select">
              <option value="">{form.role === 'ADMIN' ? t('usr.allFactories') : t('usr.notAssigned')}</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
        </div>
      </Modal>
    </div>
  );
}
